import React from "react";
import { motion } from "framer-motion";
import { Play, Pause, Download, Lock } from "lucide-react";
import { useAudio } from "../context/AudioContext";
import { formatTime, pad2 } from "../lib/format";

function Equalizer() {
  return (
    <div className="flex items-end gap-[2px] h-3.5 w-4">
      {[0, 1, 2, 3].map((i) => (
        <motion.span
          key={i}
          className="w-[2px] bg-white"
          animate={{ height: ["30%", "100%", "45%", "90%", "30%"] }}
          transition={{
            repeat: Infinity,
            duration: 0.9 + i * 0.15,
            ease: "easeInOut",
          }}
          style={{ height: "40%" }}
        />
      ))}
    </div>
  );
}

export default function Tracklist({ tracks, unlocked, onDownload, onDownloadTrack }) {
  const { currentIndex, isPlaying, playIndex } = useAudio();

  return (
    <section
      id="tracklist"
      data-testid="tracklist-section"
      className="relative w-full px-5 md:px-10 py-20 md:py-28"
    >
      <div className="max-w-4xl mx-auto w-full">
        <div className="flex items-end justify-between border-b border-zinc-800 pb-5 mb-2">
          <h2 className="font-display font-black text-3xl md:text-5xl tracking-tighter text-white">
            Tracklist
          </h2>
          <span className="font-mono text-[11px] md:text-xs uppercase tracking-widest text-zinc-500">
            {pad2(tracks.length)} Tracks
          </span>
        </div>

        <ul>
          {tracks.map((track, i) => {
            const active = currentIndex === i;
            const playing = active && isPlaying;
            return (
              <motion.li
                key={track.order}
                data-testid={`track-row-${i}`}
                initial={{ opacity: 0, y: 18 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-40px" }}
                transition={{ duration: 0.5, delay: i * 0.04, ease: [0.16, 1, 0.3, 1] }}
                onClick={() => playIndex(i)}
                className={`group relative flex items-center gap-4 md:gap-6 border-b border-zinc-900 py-4 md:py-5 cursor-pointer transition-colors ${
                  active ? "bg-zinc-900/40" : "hover:bg-zinc-900/25"
                }`}
              >
                {active && (
                  <span className="absolute left-0 top-0 bottom-0 w-[2px] bg-white" />
                )}

                {/* index / play */}
                <div className="w-8 md:w-10 flex items-center justify-center shrink-0">
                  {playing ? (
                    <Equalizer />
                  ) : (
                    <>
                      <span
                        className={`font-mono text-xs md:text-sm tracking-widest group-hover:hidden ${
                          active ? "text-white" : "text-zinc-600"
                        }`}
                      >
                        {pad2(track.order)}
                      </span>
                      <Play
                        size={15}
                        className="hidden group-hover:block text-white fill-white"
                      />
                    </>
                  )}
                </div>

                {/* title */}
                <div className="flex-1 min-w-0">
                  <p
                    className={`font-display font-bold text-lg md:text-2xl tracking-tight truncate ${
                      active ? "text-white" : "text-zinc-300 group-hover:text-white"
                    } transition-colors`}
                  >
                    {track.title}
                  </p>
                </div>

                {/* duration */}
                <span className="font-mono text-[11px] md:text-xs text-zinc-500 tabular-nums shrink-0">
                  {formatTime(track.duration)}
                </span>

                {/* download */}
                <button
                  data-testid={`track-download-${i}`}
                  aria-label={`Download ${track.title}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (unlocked) onDownloadTrack(track);
                    else onDownload();
                  }}
                  className="shrink-0 w-9 h-9 flex items-center justify-center border border-transparent text-zinc-500 hover:text-white hover:border-zinc-700 transition-colors"
                  title={unlocked ? "Download track" : "Unlock free download"}
                >
                  {unlocked ? <Download size={15} /> : <Lock size={13} />}
                </button>
              </motion.li>
            );
          })}
        </ul>

        {/* Download all */}
        <div className="mt-8 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="font-mono text-[11px] uppercase tracking-widest text-zinc-500 text-center sm:text-left">
            {unlocked
              ? "Download unlocked · grab the full tape"
              : "Enter your email once to unlock all downloads"}
          </p>
          <button
            data-testid="download-all-btn"
            onClick={onDownload}
            className="flex items-center gap-3 bg-white text-black px-6 py-3 font-mono uppercase tracking-widest text-xs hover:bg-zinc-200 transition-colors"
          >
            <Download size={14} />
            {unlocked ? "Download All" : "Free Download"}
          </button>
        </div>
      </div>
    </section>
  );
}
